import { useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Calendar, Plus, X, Sprout, CheckCircle2 } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

// Pricing matrix — keep in sync with backend SERVICE_TIERS in /app/backend/server.py
const TIERS = {
  "student-onsite":  { 60: 200, 90: 250, audience: "student", location: "onsite",  label: "Student 1-on-1 · On-Site" },
  "student-athome":  { 60: 250, 90: 300, audience: "student", location: "athome",  label: "Student 1-on-1 · At Home" },
  "student-virtual": { 60: 125, 90: 165, audience: "student", location: "virtual", label: "Student 1-on-1 · Virtual" },
  "adult-onsite":    { 60: 175, 90: 225, audience: "adult",   location: "onsite",  label: "Adult 1-on-1 · On-Site" },
  "adult-athome":    { 60: 225, 90: 275, audience: "adult",   location: "athome",  label: "Adult 1-on-1 · At Client Location" },
  "adult-virtual":   { 60: 110, 90: 145, audience: "adult",   location: "virtual", label: "Adult 1-on-1 · Virtual" },
};

const WAIVER_TEXT = `HOMESTEAD LIABILITY WAIVER & ASSUMPTION OF RISK — ONE-ON-ONE COACHING

I, the registering parent or legal guardian, acknowledge that one-on-one homestead coaching sessions take place at the working homestead leased from Ron Hylton in Yavapai County, Arizona. I understand and accept the inherent risks of a working homestead — including domestic livestock, native insects and reptiles, uneven natural terrain, tools and irrigation equipment, food-handling activities, sun and dust exposure — and assume those risks voluntarily on behalf of the registered Student.

I release Heather Mich (dba Graceful Garden Coaching), her successors and assigns, and landlord Ron Hylton from any and all liability, claims, or causes of action arising out of any loss, damage, or injury sustained during or related to the session, whether caused in whole or in part by the negligence of the released parties, to the fullest extent permitted by Arizona law.

I acknowledge that submission of the issued Graceful Garden Coaching invoice to ClassWallet within seven (7) days of the confirmed session date is solely my responsibility, and that ESA funding is between me and the State of Arizona.

By typing my full legal name below, I confirm that I have read this Waiver in its entirety, that I am 18 years of age or older, that I am the parent or legal guardian of the registered Student, and that I intend my typed name to constitute my legal signature.`;

export default function BookingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const serviceType = params.get("service") || "student-onsite";
  const durationFromQuery = parseInt(params.get("duration") || "60", 10);
  const tier = TIERS[serviceType] || TIERS["student-onsite"];
  const initialDuration = tier[durationFromQuery] ? durationFromQuery : 60;

  const [submitting, setSubmitting] = useState(false);
  const [duration, setDuration] = useState(initialDuration);
  const [form, setForm] = useState({
    student_name: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    esa_contract_number: "",
    subject: "",
    client_address: "",
    notes: "",
    waiver_signature: "",
  });

  const isAdult = tier.audience === "adult";
  const isAtHome = tier.location === "athome";
  const isVirtual = tier.location === "virtual";
  const waiverRequired = tier.location === "onsite";
  const price = useMemo(() => tier[duration], [tier, duration]);
  const [slots, setSlots] = useState([{ date: "", time: "" }]);

  const addSlot = () => {
    if (slots.length >= 3) return;
    setSlots([...slots, { date: "", time: "" }]);
  };
  const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));
  const setSlot = (i, key, val) => {
    const next = slots.slice();
    next[i] = { ...next[i], [key]: val };
    setSlots(next);
  };

  const signatureValid = waiverRequired
    ? (form.waiver_signature.trim().length >= 3 &&
       form.waiver_signature.trim().toLowerCase() === form.parent_name.trim().toLowerCase())
    : true;

  const validSlots = slots
    .filter((s) => s.date && s.time)
    .map((s) => {
      // Build ISO with Arizona offset (-07:00, no DST)
      const iso = `${s.date}T${s.time}:00-07:00`;
      const dt = new Date(iso);
      const label = dt.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Phoenix",
      });
      return { iso_datetime: iso, label };
    });

  const formValid =
    form.student_name.trim() &&
    form.parent_name.trim() &&
    form.parent_email.trim() &&
    form.parent_phone.trim() &&
    form.subject.trim() &&
    (!isAtHome || form.client_address.trim()) &&
    validSlots.length >= 1 &&
    signatureValid;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        service_type: serviceType,
        duration_minutes: duration,
        client_address: isAtHome ? form.client_address : "",
        preferred_slots: validSlots,
      });
      navigate(`/booking-thanks/${data.booking_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not submit request";
      toast.error(typeof detail === "string" ? detail : "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Link to="/services" className="inline-flex items-center gap-2 text-sm text-gg-muted hover:text-gg-green">
          <ArrowLeft size={14} /> Back To Services
        </Link>

        <header className="mt-8 fade-up d1">
          <p className="uppercase tracking-[0.22em] text-xs text-gg-brown font-semibold">
            {tier.label}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[var(--gg-text)] mt-3 leading-tight">
            {isAdult ? "Book a 1-on-1 with Heather." : "Book a private homestead session with Heather."}
          </h1>
          <p className="mt-4 text-gg-muted max-w-xl leading-relaxed">
            {isVirtual
              ? "Live online coaching, tailored to your subject. Meeting link sent after Heather confirms your slot."
              : isAtHome
              ? "Heather travels to your home or business and tailors the hour to your subject. Travel fee will be added based on your address."
              : "Tailored to whatever subject you want to dive into — gardening, baking, fiber arts, animal care, fermentation, and more. Includes a take-home curriculum packet so the learning continues at home."}
          </p>
          <div className="mt-5 rounded-xl border border-gg-border bg-gg-bg px-5 py-4 inline-flex items-center gap-5">
            <span className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
              Session length
            </span>
            <div className="flex gap-2">
              {[60, 90].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDuration(m)}
                  data-testid={`booking-duration-${m}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
                    duration === m
                      ? "bg-gg-green text-white"
                      : "bg-transparent text-gg-muted border border-gg-border hover:border-gg-green"
                  }`}
                >
                  {m} min · ${tier[m]}
                </button>
              ))}
            </div>
          </div>
          {isAtHome && (
            <p className="text-xs text-gg-muted mt-3">
              + travel fee — calculated by Heather from your address and added to the invoice on confirmation.
            </p>
          )}
        </header>

        <div className="mt-10 rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8 space-y-7 fade-up d2">
          {/* Attendee */}
          <section>
            <h2 className="font-heading text-xl text-[var(--gg-text)] mb-4">Attendee details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={isAdult ? "Your name" : "Student name"}>
                <Input
                  value={form.student_name}
                  onChange={set("student_name")}
                  data-testid="booking-student-name"
                  placeholder={isAdult ? "Your first & last name" : "Student's first & last name"}
                  autoComplete="off"
                  name="gg_student_name"
                />
              </Field>
              <Field label={isAdult ? "Billing contact (can be the same)" : "Parent / Guardian name"}>
                <Input
                  value={form.parent_name}
                  onChange={set("parent_name")}
                  data-testid="booking-parent-name"
                  placeholder={isAdult ? "Billing contact first & last name" : "Parent's first & last name"}
                  autoComplete="off"
                  name="gg_parent_name"
                />
              </Field>
              <Field label={isAdult ? "Email" : "Parent email"}>
                <Input
                  type="email"
                  value={form.parent_email}
                  onChange={set("parent_email")}
                  data-testid="booking-parent-email"
                  autoComplete="off"
                  name="gg_parent_email"
                />
              </Field>
              <Field label={isAdult ? "Phone" : "Parent phone"}>
                <Input
                  value={form.parent_phone}
                  onChange={set("parent_phone")}
                  data-testid="booking-parent-phone"
                  autoComplete="off"
                  name="gg_parent_phone"
                />
              </Field>
              <Field label="ESA contract # (optional)" hint="If using ClassWallet, providing this lets us pre-format the invoice + curriculum docs.">
                <Input
                  value={form.esa_contract_number}
                  onChange={set("esa_contract_number")}
                  placeholder="ESA-2026-000000 (optional)"
                  data-testid="booking-esa-contract"
                  autoComplete="off"
                  name="gg_esa_contract"
                />
              </Field>
              <Field
                label="Subject"
                hint="What should this session focus on? (gardening, baking, knitting, chickens…)"
              >
                <Input
                  value={form.subject}
                  onChange={set("subject")}
                  data-testid="booking-subject"
                  placeholder="e.g. Sourdough basics, or Beginner knitting"
                  autoComplete="off"
                  name="gg_subject"
                />
              </Field>
              {isAtHome && (
                <Field
                  label="Address for the visit"
                  hint="Used to calculate the travel fee."
                  className="sm:col-span-2"
                >
                  <Input
                    value={form.client_address}
                    onChange={set("client_address")}
                    data-testid="booking-client-address"
                    placeholder="Street, City, State, ZIP"
                    autoComplete="off"
                    name="gg_client_address"
                  />
                </Field>
              )}
            </div>
          </section>

          {/* Preferred slots */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl text-[var(--gg-text)]">Preferred times</h2>
              <span className="text-xs text-gg-muted">Heather will confirm one</span>
            </div>
            <div className="space-y-3">
              {slots.map((s, i) => (
                <div key={i} className="rounded-xl border border-gg-border bg-gg-bg p-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                  <Field label={`Option ${i + 1} — date`}>
                    <Input type="date" value={s.date} onChange={(e) => setSlot(i, "date", e.target.value)} data-testid={`booking-slot-date-${i}`} />
                  </Field>
                  <Field label="Time (Arizona)">
                    <Input type="time" value={s.time} onChange={(e) => setSlot(i, "time", e.target.value)} data-testid={`booking-slot-time-${i}`} />
                  </Field>
                  {slots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSlot(i)}
                      className="h-10 px-3 rounded-md border border-gg-border bg-gg-surface text-gg-brown hover:bg-[#B24C4C] hover:text-white hover:border-[#B24C4C] transition-colors"
                      aria-label="Remove this slot"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {slots.length < 3 && (
              <button
                type="button"
                onClick={addSlot}
                className="mt-3 inline-flex items-center gap-2 text-sm text-gg-green hover:text-gg-brown font-semibold"
                data-testid="booking-add-slot"
              >
                <Plus size={14} /> Suggest another time
              </button>
            )}
          </section>

          {/* Notes */}
          <section>
            <Field
              label="Anything Heather should know? (optional)"
              hint="Referred by another ESA family? Drop their first name here for a $10 credit on both of your next sessions."
            >
              <Textarea
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                placeholder="What are your student's interests, current ESA goals, or specific topics you'd like to cover? Referred by a friend? Add their first name here."
                data-testid="booking-notes"
              />
            </Field>
          </section>

          {/* Waiver — only required for on-site sessions */}
          {waiverRequired && (
            <section>
              <h2 className="font-heading text-xl text-[var(--gg-text)] mb-3">Liability waiver</h2>
              <div
                className="waiver-box rounded-xl border border-gg-border bg-gg-bg p-5 text-sm leading-relaxed whitespace-pre-wrap text-[var(--gg-text)]/90 max-h-56 overflow-y-auto"
              >
                {WAIVER_TEXT}
              </div>
              <div className="mt-3">
                <Field
                  label="Type your full legal name to sign"
                  hint={`Must match the ${isAdult ? "billing contact" : "parent"} name entered above (“${form.parent_name || "—"}”)`}
                >
                  <Input
                    value={form.waiver_signature}
                    onChange={set("waiver_signature")}
                    placeholder={form.parent_name || "Your full legal name"}
                    className="font-script text-2xl h-14"
                    data-testid="booking-waiver-signature"
                    autoComplete="off"
                    name="gg_waiver_signature"
                  />
                </Field>
                {form.waiver_signature && !signatureValid && (
                  <p className="text-xs text-[var(--gg-error)] mt-1">
                    Signature must match the {isAdult ? "billing contact" : "parent"} name entered above.
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Summary */}
          <div className="rounded-2xl bg-gg-bg border border-gg-border p-5 text-sm">
            <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">
              What you're requesting
            </p>
            <div className="flex items-center justify-between">
              <span>{tier.label} · {duration} min</span>
              <span className="font-heading text-2xl text-gg-green">${price}.00</span>
            </div>
            {isAtHome && (
              <p className="text-xs text-gg-muted mt-2">
                + travel fee calculated by Heather from your address.
              </p>
            )}
            <p className="text-xs text-gg-muted mt-2">
              Billed through a ClassWallet-ready invoice. Invoice generated only after Heather confirms a time — you won't be charged until then.
            </p>
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={!formValid || submitting}
            className="btn-pill btn-primary w-full text-base disabled:opacity-50 disabled:pointer-events-none"
            data-testid="booking-submit-btn"
          >
            {submitting ? "Sending Request…" : "Send Booking Request"} <ArrowRight size={16} />
          </button>
          <p className="text-xs text-gg-muted leading-relaxed text-center">
            By sending you agree to our{" "}
            <a className="text-gg-green hover:underline" href="/terms" target="_blank" rel="noreferrer">Terms &amp; Refund Policy</a>
            {" "}and <a className="text-gg-green hover:underline" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <Label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-gg-muted mt-1.5">{hint}</span>}
    </Label>
  );
}
